/**
 * LibraryHealthPanel — admin-only diagnostic + repair drawer.
 *
 * Surfaces every "broken row" in the DB (file_url points at a file that
 * doesn't exist on disk) and gives admins a one-click way to:
 *   - Heal everything (fix-cover-urls + remap-missing-files + auto-cover +
 *     re-extract ebook text — all in sequence)
 *   - Per-row "Replace file" upload that overwrites the missing media
 *
 * Hits these admin endpoints (all server-authenticated):
 *   GET  /api/admin/library/missing-files
 *   POST /api/admin/library/replace-file
 *   POST /api/admin/fix-cover-urls
 *   POST /api/admin/cinematic/remap-missing-files
 *   POST /api/admin/auto-cover
 *   POST /api/admin/library/extract-ebook-text
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  X, AlertTriangle, CheckCircle2, Upload, RotateCcw, Loader2, Stethoscope,
  Film, Headphones, Book,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { Button } from './ui/button';
import { authFetch } from '../utils/authUtils';

const COLLECTION_LABEL = {
  cinematic_episodes: 'Cinematic episode',
  audiobook_chapters: 'Audiobook chapter',
  content: 'Library item',
  uploaded_content: 'User upload',
};
const COLLECTION_ICON = {
  cinematic_episodes: Film,
  audiobook_chapters: Headphones,
  content: Book,
  uploaded_content: Book,
};

const LibraryHealthPanel = ({ open, onClose }) => {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [healing, setHealing] = useState(false);
  const [busyRowId, setBusyRowId] = useState(null);
  const fileInputRefs = useRef({});

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/admin/library/missing-files`);
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || `HTTP ${r.status}`);
      }
      const d = await r.json();
      setItems(d.items || []);
    } catch (e) {
      toast.error(`Health check failed: ${e.message || e}`);
    } finally {
      setLoading(false);
    }
  }, [BACKEND_URL]);

  useEffect(() => {
    if (open) refresh();
  }, [open, refresh]);

  // One-click healer — runs the four rescue endpoints sequentially with a
  // single live-updating toast. Each phase is a no-op when its backlog is 0.
  const handleHealAll = async () => {
    if (healing) return;
    setHealing(true);
    const tid = toast.loading('Healing library…', {
      duration: 90000,
      position: 'bottom-right',
    });
    const summary = {
      fixed_urls: 0,
      remapped: 0,
      covers: 0,
      texts: 0,
      errors: [],
    };
    try {
      // 1. Strip /api/api/ prefixes
      toast.loading('Fixing cover URLs…', { id: tid });
      try {
        const r = await authFetch(`${BACKEND_URL}/api/admin/fix-cover-urls`, { method: 'POST' });
        const d = await r.json().catch(() => ({}));
        if (r.ok) summary.fixed_urls = d.fixed || 0;
        else summary.errors.push(`urls: ${d.detail || r.status}`);
      } catch (e) { summary.errors.push(`urls: ${e.message}`); }

      // 2. Remap missing cinematic files
      toast.loading(`URLs ${summary.fixed_urls}. Remapping cinematics…`, { id: tid });
      try {
        const r = await authFetch(`${BACKEND_URL}/api/admin/cinematic/remap-missing-files`, { method: 'POST' });
        const d = await r.json().catch(() => ({}));
        if (r.ok) summary.remapped = d.fixed || 0;
        else summary.errors.push(`remap: ${d.detail || r.status}`);
      } catch (e) { summary.errors.push(`remap: ${e.message}`); }

      // 3. Generate missing covers
      toast.loading(`Remapped ${summary.remapped}. Painting covers…`, { id: tid });
      try {
        const r = await authFetch(`${BACKEND_URL}/api/admin/auto-cover`, { method: 'POST' });
        const d = await r.json().catch(() => ({}));
        if (r.ok) summary.covers = d.updated || 0;
        else summary.errors.push(`cover: ${d.detail || r.status}`);
      } catch (e) { summary.errors.push(`cover: ${e.message}`); }

      // 4. Extract missing ebook text
      toast.loading(`Covers ${summary.covers}. Extracting ebook text…`, { id: tid });
      try {
        const r = await authFetch(`${BACKEND_URL}/api/admin/library/extract-ebook-text`, { method: 'POST' });
        const d = await r.json().catch(() => ({}));
        if (r.ok) summary.texts = d.extracted || 0;
        else summary.errors.push(`text: ${d.detail || r.status}`);
      } catch (e) { summary.errors.push(`text: ${e.message}`); }

      const fmt = `Healed ✦ ${summary.fixed_urls} URLs · ${summary.remapped} files · ${summary.covers} covers · ${summary.texts} ebooks`;
      if (summary.errors.length) {
        toast.error(`${fmt}\n${summary.errors.join('; ')}`, { id: tid, duration: 8000 });
      } else {
        toast.success(fmt, { id: tid, duration: 5000 });
      }
      await refresh();
    } finally {
      setHealing(false);
    }
  };

  const handleReplaceClick = (rowKey) => {
    const input = fileInputRefs.current[rowKey];
    if (input) input.click();
  };

  const handleFilePicked = async (item, fileList) => {
    const file = fileList && fileList[0];
    if (!file) return;
    const rowKey = `${item.collection}-${item.id}`;
    setBusyRowId(rowKey);
    const tid = toast.loading(`Uploading ${file.name}…`, { position: 'bottom-right' });
    try {
      const fd = new FormData();
      fd.append('collection', item.collection);
      fd.append('id', item.id);
      fd.append('file', file);
      const r = await authFetch(`${BACKEND_URL}/api/admin/library/replace-file`, {
        method: 'POST',
        body: fd,
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`);
      toast.success('File replaced — row is now healthy', { id: tid });
      // Drop the row from the list immediately for snappy UX.
      setItems((curr) => curr.filter((r) => `${r.collection}-${r.id}` !== rowKey));
    } catch (e) {
      toast.error(`Replace failed: ${e.message || e}`, { id: tid });
    } finally {
      setBusyRowId(null);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-4"
      data-testid="library-health-panel"
    >
      <div className="bg-slate-900 border border-slate-700 rounded-t-2xl sm:rounded-2xl shadow-2xl w-full max-w-3xl max-h-[92vh] sm:max-h-[85vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-slate-800">
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-10 w-10 rounded-xl bg-rose-500/15 text-rose-300 flex items-center justify-center flex-shrink-0">
              <Stethoscope className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <p className="text-[11px] uppercase tracking-[0.2em] text-rose-300 font-semibold">
                Library Health
              </p>
              <p className="text-base font-bold text-white truncate">
                {loading
                  ? 'Scanning…'
                  : items.length === 0
                    ? 'Everything is healthy ✓'
                    : `${items.length} broken row${items.length === 1 ? '' : 's'} detected`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button
              variant="ghost"
              size="sm"
              onClick={refresh}
              disabled={loading}
              className="text-gray-300 hover:text-white"
              data-testid="library-health-refresh"
            >
              <RotateCcw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-400 hover:text-white p-1"
              data-testid="library-health-close"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Heal-all banner */}
        <div className="p-5 border-b border-slate-800 bg-gradient-to-r from-rose-500/5 to-amber-500/5">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-300 mt-0.5 flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-white">
                One-click heal
              </p>
              <p className="text-xs text-gray-400 mt-1">
                Fixes broken cover URLs, remaps missing cinematic files, paints
                missing covers, and re-extracts ebook text — all in sequence.
                Safe to run repeatedly.
              </p>
            </div>
            <Button
              onClick={handleHealAll}
              disabled={healing}
              className="bg-rose-600 hover:bg-rose-500 text-white text-sm font-semibold flex-shrink-0"
              data-testid="library-health-heal-all"
            >
              {healing ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Stethoscope className="w-4 h-4 mr-1" />
                  Heal everything
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Broken rows list */}
        <div className="flex-1 overflow-y-auto">
          {loading && items.length === 0 ? (
            <div className="p-12 text-center text-gray-400">
              <Loader2 className="w-8 h-8 mx-auto animate-spin mb-3" />
              Scanning collections…
            </div>
          ) : items.length === 0 ? (
            <div className="p-12 text-center" data-testid="library-health-empty">
              <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-400 mb-3" />
              <p className="text-white font-semibold">No broken rows</p>
              <p className="text-gray-400 text-sm mt-1">
                Every media row points at a file that exists on disk.
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-slate-800">
              {items.map((it) => {
                const rowKey = `${it.collection}-${it.id}`;
                const ICon = COLLECTION_ICON[it.collection] || Book;
                const busy = busyRowId === rowKey;
                return (
                  <li key={rowKey} className="p-4 hover:bg-slate-800/40 transition-colors">
                    <div className="flex items-center gap-3">
                      <div className="h-9 w-9 rounded-lg bg-amber-500/15 text-amber-300 flex items-center justify-center flex-shrink-0">
                        <ICon className="w-4 h-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-white truncate">
                          {it.title || '(untitled)'}
                          {it.episode_number != null && (
                            <span className="text-gray-400 font-normal ml-1.5">
                              · #{it.episode_number}
                            </span>
                          )}
                        </p>
                        <p className="text-[11px] text-gray-500 truncate font-mono">
                          {COLLECTION_LABEL[it.collection]} · {it.id}
                        </p>
                        <p className="text-[10px] text-rose-300/80 truncate mt-0.5 font-mono">
                          missing → {it.file_url || it.file_path || '(no path)'}
                        </p>
                      </div>
                      <div className="flex-shrink-0">
                        <input
                          ref={(el) => {
                            fileInputRefs.current[rowKey] = el;
                          }}
                          type="file"
                          accept="audio/*,video/*"
                          className="hidden"
                          onChange={(e) => handleFilePicked(it, e.target.files)}
                          data-testid={`library-health-file-input-${it.id}`}
                        />
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={busy}
                          onClick={() => handleReplaceClick(rowKey)}
                          className="bg-slate-800 border-slate-600 hover:bg-slate-700 text-white text-xs"
                          data-testid={`library-health-replace-${it.id}`}
                        >
                          {busy ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <>
                              <Upload className="w-3.5 h-3.5 mr-1" />
                              Replace
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default LibraryHealthPanel;
